import gsap from "@lib/gsap";
import { Resize } from "@lib/subs";
import { isWebflowEditor } from "@/webflow/detect-editor";
import { onDestroy } from "@/modules/_";

/**
 * CMX Systems — Desktop: fan (rotate + base X in JS), hover spread on siblings, pipe → tag chips.
 *
 * Multiple `data-module="cmx-system-cards"`: each matching element runs this function, but only
 * one init runs **per `.swiper`**: the first matching node in document order sets `data-cmx-spread-init` on
 * that swiper; later duplicates on other slides no-op. A **second slider** elsewhere on the page uses a
 * different `.swiper`, so it initializes separately. Prefer a single `data-module` on the slider wrapper.
 *
 * Card colors / tags / layout: Webflow CSS (no transform or transition on cards — GSAP owns motion, ease power4.inOut).
 * Tags: `|` in `.plain-text` only; idempotent with `is-split` / existing `.cmx-systems_tag`.
 */

const CMX_SPREAD_INIT = "data-cmx-spread-init";

function resolveSwiperRoot(el: HTMLElement): HTMLElement | null {
  return el.querySelector<HTMLElement>(".swiper") ?? el.closest<HTMLElement>(".swiper");
}

/** Must stay in sync with Webflow/CSS fan layout (nth-child order). */
const CARD_FAN = [
  { rot: -2.363, x: 54 },
  { rot: -1.035, x: 20 },
  { rot: 1.04, x: -20 },
  { rot: 2.911, x: -54 },
] as const;

function fanAt(index: number): { rot: number; x: number } {
  return CARD_FAN[index] ?? { rot: 0, x: 0 };
}

const DESKTOP_MIN = 992;

/**
 * Hover spread (px), by distance from hovered slide (`steps = |j - hovered|`):
 * `|dx| = ADJACENT_SPREAD + (steps - 1) * OUTER_STEP`
 * — Bigger opening next to the hovered card; each further card adds only OUTER_STEP (same +5 rhythm as before).
 */
const ADJACENT_SPREAD = 18;
const OUTER_STEP = 5;

const STAGGER_IN = 0.055;
const STAGGER_OUT = 0.045;
const DURATION_IN = 0.42;
const DURATION_OUT = 0.48;

const IS_DESKTOP = () => window.innerWidth >= DESKTOP_MIN;

/** Extra delta X added on top of fan `x` (px). */
function spreadDeltas(hovered: number, count: number): { index: number; dx: number }[] {
  const out: { index: number; dx: number }[] = [];
  for (let j = 0; j < count; j++) {
    if (j === hovered) continue;
    const dir = j < hovered ? -1 : 1;
    const steps = Math.abs(j - hovered);
    const mag = ADJACENT_SPREAD + (steps - 1) * OUTER_STEP;
    const dx = dir * mag;
    out.push({ index: j, dx });
  }
  out.sort((a, b) => Math.abs(a.index - hovered) - Math.abs(b.index - hovered));
  return out;
}

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  if (isWebflowEditor()) return;

  const swiperEl = resolveSwiperRoot(element);
  if (!swiperEl) return;

  if (swiperEl.hasAttribute(CMX_SPREAD_INIT)) return;
  swiperEl.setAttribute(CMX_SPREAD_INIT, "");

  const slides = Array.from(swiperEl.querySelectorAll<HTMLElement>(".swiper-slide"));
  const cards = slides.map((s) => s.querySelector<HTMLElement>(".cmx-systems_card"));

  if (!cards.length) {
    swiperEl.removeAttribute(CMX_SPREAD_INIT);
    return;
  }

  const n = cards.length;

  swiperEl.querySelectorAll<HTMLElement>(".cmx-systems_tags .plain-text").forEach((el) => {
    const wrap = el.closest(".cmx-systems_tags");
    if (wrap?.classList.contains("is-split")) return;
    if (el.querySelector(".cmx-systems_tag")) {
      wrap?.classList.add("is-split");
      return;
    }

    const raw = el.textContent?.trim() ?? "";
    if (!raw) return;

    const parts = raw
      .split("|")
      .map((t) => t.trim())
      .filter(Boolean);
    if (!parts.length) return;

    el.innerHTML = parts.map((tag) => `<span class="cmx-systems_tag">${tag}</span>`).join("");
    wrap?.classList.add("is-split");
  });

  const cardEls = cards.filter((c): c is HTMLElement => !!c);

  function applyFanBaseline() {
    if (!IS_DESKTOP()) {
      cardEls.forEach((card) => {
        card.style.transition = "";
        gsap.set(card, { clearProps: "transform" });
      });
      return;
    }

    cardEls.forEach((card) => {
      card.style.transition = "none";
    });

    cards.forEach((card, i) => {
      if (!card) return;
      const f = fanAt(i);
      gsap.set(card, {
        rotation: f.rot,
        x: f.x,
        y: 0,
        yPercent: 0,
        scale: 1,
        transformOrigin: "50% 50%",
      });
    });
  }

  applyFanBaseline();

  const hoverCleanups: Array<() => void> = [];

  slides.forEach((slide, i) => {
    const card = cards[i];
    if (!card) return;

    const cardEl = card;

    function onEnter() {
      if (!IS_DESKTOP()) return;

      gsap.killTweensOf(cardEls);

      const f = fanAt(i);
      gsap.to(cardEl, {
        rotation: f.rot,
        x: f.x,
        y: 0,
        yPercent: -1,
        scale: 1,
        duration: DURATION_IN,
        ease: "power4.inOut",
      });

      spreadDeltas(i, n).forEach((t, idx) => {
        const c = cards[t.index];
        if (!c) return;
        const base = fanAt(t.index);
        gsap.to(c, {
          rotation: base.rot,
          x: base.x + t.dx,
          y: 0,
          yPercent: 0,
          scale: 1,
          duration: DURATION_IN,
          ease: "power4.inOut",
          delay: idx * STAGGER_IN,
        });
      });
    }

    function onLeave() {
      if (!IS_DESKTOP()) return;

      gsap.killTweensOf(cardEls);

      const f = fanAt(i);
      gsap.to(cardEl, {
        rotation: f.rot,
        x: f.x,
        y: 0,
        yPercent: 0,
        scale: 1,
        duration: DURATION_OUT,
        ease: "power4.inOut",
      });

      const order = spreadDeltas(i, n)
        .map((t) => t.index)
        .reverse();
      order.forEach((j, idx) => {
        const c = cards[j];
        if (!c) return;
        const base = fanAt(j);
        gsap.to(c, {
          rotation: base.rot,
          x: base.x,
          y: 0,
          yPercent: 0,
          scale: 1,
          duration: DURATION_OUT,
          ease: "power4.inOut",
          delay: idx * STAGGER_OUT,
        });
      });
    }

    slide.addEventListener("mouseenter", onEnter);
    slide.addEventListener("mouseleave", onLeave);

    hoverCleanups.push(() => {
      slide.removeEventListener("mouseenter", onEnter);
      slide.removeEventListener("mouseleave", onLeave);
    });
  });

  const removeResize = Resize.add(() => {
    applyFanBaseline();
  });

  onDestroy(() => {
    removeResize();
    hoverCleanups.forEach((fn) => fn());
    gsap.killTweensOf(cardEls);
    cardEls.forEach((card) => {
      card.style.transition = "";
      gsap.set(card, { clearProps: "all" });
    });
    swiperEl.removeAttribute(CMX_SPREAD_INIT);
  });
}
